import { ActionReducer } from '@ngrx/store';
import { PatientIdChangeTrigger } from '../../../user/data/patient-id-change-trigger';
import { setPatientIdSuccess } from '../../../user/store/actions/user.actions';
import { tourProgressFeatureKey } from '../../../tours/store/reducer/tour-progress.reducer';

// Keys preserved when an in-account patient switch commits (setPatientIdSuccess, trigger 'switch'):
//   router        — the deterministic switch policy remaps the route; the router slice must survive so
//                   the new URL is applied rather than lost.
//   app           — user-independent settings (theme, browserName, externalUrls) with no patient data.
//   user          — the logged-in user is unchanged across an in-account switch; preserving it also
//                   lets the userReducer process setPatientIdSuccess and adopt the new patientId.
//   tour-progress — user-scoped, not patient-scoped: the API keys tour progress on the portal user, and
//                   a caregiver switching between patients has not un-seen the portal's tours. It is
//                   also loaded only on getUserSuccess, which a patient switch does not re-trigger, so
//                   clearing it would leave isLoaded false and suppress every tour for the rest of the
//                   session.
//   notification-preferences
//                 — user-scoped: notification consent and the contact details it is delivered to are
//                   keyed on the portal user, so a caregiver switching patients sees the same
//                   preferences and has not changed what they consented to. Registering the slice at
//                   app level is not sufficient on its own — this list resets every root key it does
//                   not name, wherever the slice was registered.
// Every other slice is patient-scoped, so it is reset to its initialState. This is the single source of
// truth for patient-switch reset — a newly added *patient* slice clears automatically, with nothing to
// remember to wire up per reducer. A newly added *user*-scoped slice must be added here.
//
// The literal string, not the feature's exported key: this file is under core/, which the lint
// boundary forbids from importing feature store modules. reset-patient-state.integration.spec.ts
// asserts the slice actually survives a switch, so the two cannot drift silently.
const PRESERVED_KEYS = ['router', 'app', 'user', tourProgressFeatureKey, 'notification-preferences'] as const;

export function resetPatientStateMetaReducer<T>(reducer: ActionReducer<T>): ActionReducer<T> {
  return (state, action) => {
    // Only a real switch resets. setPatientIdSuccess also commits on a page load (AppComponent
    // replaying the patientId in the URL), where there is no previous patient's data to drop — the
    // store is fresh from bootstrap — and wiping every slice would discard whatever the loading
    // page has already fetched, with no patientSwitchInitiated to make it refetch (bug 57454).
    const trigger = (action as { trigger?: PatientIdChangeTrigger }).trigger;
    if (action.type === setPatientIdSuccess.type && trigger !== 'page-load') {
      const source = state as unknown as Record<string, unknown> | undefined;
      const preservedState = (PRESERVED_KEYS as readonly string[]).reduce(
        (acc, key) => {
          acc[key] = source?.[key];
          return acc;
        },
        {} as Record<string, unknown>
      );
      return reducer(preservedState as unknown as T, action);
    }
    return reducer(state, action);
  };
}
